import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../App.css";

export default function Financieel() {
    const [apartments, setApartments] = useState([]);
    const [finances, setFinances] = useState([]);
    const [file, setFile] = useState(null);
    const [formData, setFormData] = useState({
        apartment_id: "",
        owner_name: "",
        tenant_name: "",
        check_in_date: "",
        check_out_date: "",
        amount_paid: "",
        agent_expenses: "",
        expenses_description: "",
        currency: "$",
    });

    const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const APARTMENTS_API = `${API_BASE}/api/apartments`;
    const FINANCES_API = `${API_BASE}/api/finances`;

    useEffect(() => {
        fetchApartments();
        fetchFinances();
    }, []);

    const fetchApartments = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(APARTMENTS_API, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setApartments(res.data);
        } catch (err) {
            console.error("Error fetching apartments:", err);
            alert("❌ Failed to load apartments. Check your login or server.");
        }
    };


    const fetchFinances = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(FINANCES_API, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFinances(res.data);
        } catch (err) {
            console.error("Error fetching finances:", err);
            alert("❌ Failed to load finances. Please check server or token.");
        }
    };

    const ownerByApartmentId = useMemo(() => {
        const map = {};
        apartments.forEach(ap => {
            map[ap.id] = ap.eigenaar || ap.owner || "";
        });
        return map;
    }, [apartments]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updated = { ...formData, [name]: value };
        if (name === "apartment_id") {
            const selectedApartment = apartments.find(a => a.id === parseInt(value));
            updated.owner_name = selectedApartment?.owner_name || "";
        }
        setFormData(updated);
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0] || null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem("token");
        if (!token) {
            alert("⚠️ You must be logged in to save a record.");
            return;
        }

        try {
            const send = new FormData();
            Object.entries(formData).forEach(([k, v]) => send.append(k, v));
            if (file) send.append("proof", file);

            await axios.post(FINANCES_API, send, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            });

            alert("✅ Finance record saved successfully!");
            await fetchFinances();

            setFormData({
                apartment_id: "",
                owner_name: "",
                tenant_name: "",
                check_in_date: "",
                check_out_date: "",
                amount_paid: "",
                agent_expenses: "",
                expenses_description: "",
                currency: "$",
            });
            setFile(null);
        } catch (err) {
            console.error("Error saving finance:", err);
            const msg =
                err.response?.data?.message ||
                err.message ||
                "Server error while saving finance record.";
            alert(`❌ ${msg}`);
        }
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem("token");
        if (!token) {
            alert("⚠️ You must be logged in to delete a record.");
            return;
        }

        if (!window.confirm("Are you sure you want to delete this record?")) return;

        try {
            await axios.delete(`${FINANCES_API}/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            alert("🗑️ Finance record deleted successfully!");
            await fetchFinances();
        } catch (err) {
            console.error("Error deleting finance:", err);
            const msg =
                err.response?.data?.message ||
                err.message ||
                "Server error while deleting finance record.";
            alert(`❌ ${msg}`);
        }
    };

    const calculateRemaining = (paid, expenses) => {
        const a = Number(paid || 0);
        const e = Number(expenses || 0);
        return (a - e).toFixed(2);
    };

    return (
        <div className="finance-page">
            <h2 className="page-title"> Financial Management</h2>

            <form className="finance-form glass" onSubmit={handleSubmit}>
                <div className="form-grid">
                    <label>
                        Apartment
                        <select name="apartment_id" value={formData.apartment_id} onChange={handleChange} required>
                            <option value="">-- Select apartment --</option>
                            {apartments.map(ap => (
                                <option key={ap.id} value={ap.id}>{ap.number}</option>
                            ))}
                        </select>
                    </label>

                    <label>
                        Owner
                        <input type="text" name="owner_name" value={formData.owner_name} onChange={handleChange} placeholder="Auto-filled" />
                    </label>

                    <label>
                        Tenant
                        <input type="text" name="tenant_name" value={formData.tenant_name} onChange={handleChange} />
                    </label>

                    {/* Check-in & Check-out naast elkaar */}
                    <div className="date-row">
                        <label>
                            Check-in
                            <input type="date" name="check_in_date" value={formData.check_in_date} onChange={handleChange} />
                        </label>
                        <label>
                            Check-out
                            <input type="date" name="check_out_date" value={formData.check_out_date} onChange={handleChange} />
                        </label>
                    </div>

                    <label>
                        Currency
                        <select name="currency" value={formData.currency} onChange={handleChange} required>
                            <option value="$">$ (US Dollar)</option>
                        </select>
                    </label>

                    <label>
                        Amount paid
                        <input
                            type="number"
                            name="amount_paid"
                            value={formData.amount_paid}
                            onChange={handleChange}
                            required
                        />
                    </label>

                    <label>
                        Agent expenses
                        <input
                            type="number"
                            name="agent_expenses"
                            value={formData.agent_expenses}
                            onChange={handleChange}
                        />
                    </label>

                    <label className="col-span-2">
                        Expenses description
                        <textarea name="expenses_description" value={formData.expenses_description} onChange={handleChange} rows={3}></textarea>
                    </label>

                    <label>
                        Proof (optional)
                        <input type="file" accept="image/*" onChange={handleFileChange} />
                    </label>
                </div>

                <div className="actions">
                    <button type="submit" className="btn-primary">💾 Save Record</button>
                </div>
            </form>

            {/* Table Section (desktop) */}
            <div className="finance-table glass">
                <table>
                    <thead>
                        <tr>
                            <th>Apartment</th>
                            <th>Owner</th>
                            <th>Tenant + Check-in/out</th>
                            <th>Paid</th>
                            <th>Expenses</th>
                            <th>Remaining</th>
                            <th>Date</th>
                            <th>Proof</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {finances.map((f) => (
                            <tr key={f.id}>
                                <td>{f.apartment_number}</td>
                                <td>{f.owner_name || "—"}</td>
                                <td>
                                    <strong>{f.tenant_name || "—"}</strong>
                                    <div className="subtext">
                                        {(f.check_in_date || "")}{" "}
                                        {f.check_out_date ? `→ ${f.check_out_date}` : ""}
                                    </div>
                                </td>
                                <td>{f.currency || "₺"}{f.amount_paid ?? "0"}</td>
                                <td>{f.currency || "₺"}{f.agent_expenses ?? "0"}</td>
                                <td>{f.currency || "₺"}{calculateRemaining(f.amount_paid, f.agent_expenses)}</td>
                                <td className="cell-nowrap">{(f.created_at && new Date(f.created_at).toLocaleDateString()) || "—"}</td>
                                <td>
                                    {f.proof ? (
                                        <a href={`${API_BASE}/uploads/${f.proof}`} target="_blank" rel="noreferrer">View</a>
                                    ) : "—"}
                                </td>
                                <td>
                                    <button className="btn-danger sm" onClick={() => handleDelete(f.id)}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Card Section (mobile) */}
            <div className="finance-cards">
                {finances.map((f) => (
                    <div className="finance-card" key={f.id}>
                        <div className="row">
                            <span className="label">Apartment</span>
                            <span className="value">{f.apartment_number}</span>
                        </div>
                        <div className="row">
                            <span className="label">Owner</span>
                            <span className="value">{f.owner_name || "—"}</span>
                        </div>
                        <div className="row">
                            <span className="label">Tenant</span>
                            <span className="value">{f.tenant_name || "—"}</span>
                        </div>
                        <div className="row">
                            <span className="label">Check-in</span>
                            <span className="value cell-nowrap">
                                {f.check_in_date
                                    ? new Date(f.check_in_date).toLocaleDateString()
                                    : "—"}
                            </span>
                        </div>
                        <div className="row">
                            <span className="label">Check-out</span>
                            <span className="value cell-nowrap">
                                {f.check_out_date
                                    ? new Date(f.check_out_date).toLocaleDateString()
                                    : "—"}
                            </span>
                        </div>
                        <div className="row">
                            <span className="label">Paid</span>
                            <span className="value amount">
                                {f.currency || "₺"}
                                {f.amount_paid ?? "0"}
                            </span>
                        </div>
                        <div className="row">
                            <span className="label">Remaining</span>
                            <span className="value amount">
                                {f.currency || "₺"}
                                {calculateRemaining(f.amount_paid, f.agent_expenses)}
                            </span>
                        </div>
                        <div className="row">
                            <span className="label">Date</span>
                            <span className="value cell-nowrap">
                                {f.created_at
                                    ? new Date(f.created_at).toLocaleDateString()
                                    : "—"}
                            </span>
                        </div>
                        <div className="row">
                            <span className="label">Proof</span>
                            <span className="value">
                                {f.proof ? (
                                    <a
                                        href={`${API_BASE}/uploads/${f.proof}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View
                                    </a>
                                ) : (
                                    "—"
                                )}
                            </span>
                        </div>
                        <div className="actions">
                            <button
                                className="btn-danger sm"
                                type="button"
                                onClick={() => handleDelete(f.id)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

